/**
 * AddProjectForm Component
 * 
 * Form for creating and editing projects.
 * Handles project title, overview, and image management.
 * 
 * Features:
 * - Create new projects
 * - Edit existing projects
 * - Add images with descriptions
 * - Remove images
 * - Form validation
 * - Save to Firebase
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { saveProject, updateProject } from './utilities/projectsUtils';
import { getImage } from '../../Common/Images/utilities/imageUtils';
import ProjectImageSelector from './ProjectImageSelector';
import { rewriteProjectWithAI } from '../../AI/utilities/aiLogicService';
import { loadUserSkills } from '../Skills/utilities/skillsUtils';
import SkillsGallery from '../Skills/SkillsGallery';
import { useFixedMenuHeaderActions } from '../../Common/Menus/FixedMenu';

const IMAGE_DESCRIPTION_MAX_LENGTH = 160;

const sanitizeImageDescription = (description = '') => description.slice(0, IMAGE_DESCRIPTION_MAX_LENGTH);

const normalizeProjectUrl = (value = '') => {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  if (!trimmed) {
    return '';
  }

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  return `https://${trimmed}`;
};

const isValidProjectUrl = (value = '') => {
  if (!value) {
    return true;
  }

  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch (error) {
    return false;
  }
};

const AddProjectForm = ({
  userId,
  editingProject,
  restoredDraft,
  onDraftChange,
  onProjectSaved,
  onCancel,
  onOpenAI,
}) => {
  const restoredDraftRef = useRef(restoredDraft);
  const [title, setTitle] = useState('');
  const [overview, setOverview] = useState('');
  const [projectUrl, setProjectUrl] = useState('');
  const [images, setImages] = useState([]);
  const [skillsUsed, setSkillsUsed] = useState([]);
  const [availableSkills, setAvailableSkills] = useState([]);
  const [showSkillsMenu, setShowSkillsMenu] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeFormTab, setActiveFormTab] = useState('details');
  const [fieldErrors, setFieldErrors] = useState({});
  const titleInputRef = useRef(null);
  const overviewInputRef = useRef(null);
  const urlInputRef = useRef(null);

  // Initialize form with editing project data
  useEffect(() => {
    const source = restoredDraftRef.current || editingProject;
    if (source) {
      setTitle(source.title || '');
      setOverview(source.overview || '');
      setProjectUrl(source.projectUrl || source.linkUrl || '');
      setImages(source.images || []);
      setSkillsUsed(Array.isArray(source.skillsUsed) ? source.skillsUsed : []);
    } else {
      setTitle('');
      setOverview('');
      setProjectUrl('');
      setImages([]);
      setSkillsUsed([]);
    }
    setError(null);
    setFieldErrors({});
    setActiveFormTab('details');
  }, [editingProject]);

  useEffect(() => {
    onDraftChange?.({ title, overview, projectUrl, images, skillsUsed });
  }, [images, onDraftChange, overview, projectUrl, skillsUsed, title]);

  useEffect(() => {
    let active = true;
    if (!userId) return undefined;
    loadUserSkills(userId)
      .then((skills) => { if (active) setAvailableSkills(skills || []); })
      .catch(() => { if (active) setAvailableSkills([]); });
    return () => { active = false; };
  }, [userId]);

  /**
   * Handle adding an image with description
   */
  const handleAddImage = (imageItem) => {
    const sanitizedImageItem = {
      ...imageItem,
      description: sanitizeImageDescription(imageItem?.description || ''),
    };
    setImages((currentImages) => [...currentImages, sanitizedImageItem]);
  };

  /**
   * Handle removing an image from the list
   */
  const handleRemoveImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  /**
   * Handle updating image description
   */
  const handleUpdateImageDescription = (index, description) => {
    const updatedImages = [...images];
    updatedImages[index].description = sanitizeImageDescription(description);
    setImages(updatedImages);
  };

  /**
   * Handle AI rewrite
   */
  const handleAIRewrite = useCallback(() => {
    if (!title.trim() && !overview.trim()) {
      setError('Add a title or description before asking AI to rewrite it.');
      return;
    }
    setError(null);
    const currentDraft = { title, overview, projectUrl, images, skillsUsed };
    onOpenAI?.({
      kind: 'project',
      label: 'Rewrite project',
      contextLabel: title.trim() || 'New project',
      placeholder: 'Describe how this project wording should improve...',
      generate: (request) => rewriteProjectWithAI({
        title,
        overview,
        images: images.map((image) => ({
          imageId: image.imageId,
          description: image.description || '',
        })),
      }, request),
      onApply: (rewritten) => {
        const rewrittenImages = Array.isArray(rewritten.images)
          ? currentDraft.images.map((image) => {
            const rewrite = rewritten.images.find((item) => item.imageId === image.imageId);
            return rewrite
              ? { ...image, description: sanitizeImageDescription(rewrite.description) }
              : image;
          })
          : currentDraft.images;
        onDraftChange?.({
          ...currentDraft,
          title: rewritten.title || currentDraft.title,
          overview: rewritten.overview || currentDraft.overview,
          images: rewrittenImages,
        });
      },
    });
  }, [images, onDraftChange, onOpenAI, overview, projectUrl, skillsUsed, title]);

  /**
   * Validate form
   */
  const validateForm = () => {
    const nextErrors = {};
    if (!title.trim()) {
      nextErrors.title = 'Enter a project title.';
    }

    if (!overview.trim()) {
      nextErrors.overview = 'Enter a project overview.';
    }

    const normalizedUrl = normalizeProjectUrl(projectUrl);
    if (normalizedUrl && !isValidProjectUrl(normalizedUrl)) {
      nextErrors.projectUrl = 'Enter a valid HTTP or HTTPS link.';
    }
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      setActiveFormTab('details');
      setError('Review the highlighted fields before saving.');
      window.setTimeout(() => {
        if (nextErrors.title) titleInputRef.current?.focus();
        else if (nextErrors.overview) overviewInputRef.current?.focus();
        else urlInputRef.current?.focus();
      }, 0);
      return false;
    }
    return true;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    setSaving(true);

    try {
      const normalizedUrl = normalizeProjectUrl(projectUrl);
      const projectData = {
        title: title.trim(),
        overview: overview.trim(),
        projectUrl: normalizedUrl,
        skillsUsed,
        images: images.map((imageItem) => ({
          ...imageItem,
          description: sanitizeImageDescription(imageItem?.description || ''),
        })),
      };

      if (editingProject) {
        // Update existing project
        await updateProject(userId, editingProject.id, projectData);
        onProjectSaved(`Project "${title}" updated successfully`);
      } else {
        // Create new project with timestamp as ID
        const projectId = `project_${Date.now()}`;
        await saveProject(userId, projectId, projectData);
        onProjectSaved(`Project "${title}" created successfully`);
      }
    } catch (err) {
      console.error('Error saving project:', err);
      setError(err.message || 'Failed to save project');
    } finally {
      setSaving(false);
    }
  };

  const detailsComplete = Boolean(title.trim() && overview.trim()
    && (!normalizeProjectUrl(projectUrl) || isValidProjectUrl(normalizeProjectUrl(projectUrl))));
  const hasDetailsErrors = Object.values(fieldErrors).some(Boolean);
  const headerActions = useMemo(() => (
    <button type="button" className="btn btn-sm btn-outline-primary fixed-menu-ai-action" onClick={handleAIRewrite} disabled={saving || !onOpenAI} title="Rewrite with AI">
      <i className="bi bi-stars" aria-hidden="true"></i>
      <span>Rewrite with AI</span>
    </button>
  ), [handleAIRewrite, onOpenAI, saving]);
  useFixedMenuHeaderActions(headerActions);

  return (
    <form className="project-form portfolio-record-form portfolio-tabbed-form" onSubmit={handleSubmit} noValidate>
        <div className="portfolio-form-tabs" role="tablist" aria-label="Project form sections">
          <button id="project-details-tab" type="button" role="tab" aria-selected={activeFormTab === 'details'} aria-controls="project-details-panel" className={activeFormTab === 'details' ? 'active' : ''} onClick={() => setActiveFormTab('details')}>
            Details
            {hasDetailsErrors ? <i className="bi bi-exclamation-circle-fill text-danger" aria-label="Needs attention"></i> : detailsComplete ? <i className="bi bi-check-circle-fill text-success" aria-label="Complete"></i> : null}
          </button>
          <button id="project-skills-tab" type="button" role="tab" aria-selected={activeFormTab === 'skills'} aria-controls="project-skills-panel" className={activeFormTab === 'skills' ? 'active' : ''} onClick={() => setActiveFormTab('skills')}>
            Skills <span className="portfolio-tab-count" aria-label={`${skillsUsed.length} selected`}>{skillsUsed.length}</span>
          </button>
          <button id="project-images-tab" type="button" role="tab" aria-selected={activeFormTab === 'images'} aria-controls="project-images-panel" className={activeFormTab === 'images' ? 'active' : ''} onClick={() => setActiveFormTab('images')}>
            Images <span className="portfolio-tab-count" aria-label={`${images.length} selected`}>{images.length}</span>
          </button>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label="Close"
            />
          </div>
        )}

        {activeFormTab === 'details' ? <section className="portfolio-form-tab-panel" id="project-details-panel" role="tabpanel" aria-labelledby="project-details-tab">
          <div className="portfolio-form-section-heading">
            <span className="portfolio-form-section-icon"><i className="bi bi-card-text" aria-hidden="true"></i></span>
            <div><h4 id="project-details-heading">Project details</h4><p>Give visitors a clear, concise overview.</p></div>
          </div>
        <div className="mb-3">
          <label htmlFor="projectTitle" className="form-label fw-bold">
            Project Title <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className="form-control"
            id="projectTitle"
            ref={titleInputRef}
            value={title}
            onChange={(e) => { setTitle(e.target.value); setFieldErrors((current) => ({ ...current, title: undefined })); }}
            placeholder="Enter project title"
            maxLength={100}
            aria-invalid={Boolean(fieldErrors.title)}
            disabled={saving}
          />
          {fieldErrors.title ? <div className="invalid-feedback d-block">{fieldErrors.title}</div> : null}
        </div>

        <div className="mb-3">
          <label htmlFor="projectOverview" className="form-label fw-bold">
            Project Overview <span className="text-danger">*</span>
          </label>
          <textarea
            className="form-control"
            id="projectOverview"
            ref={overviewInputRef}
            value={overview}
            onChange={(e) => { setOverview(e.target.value); setFieldErrors((current) => ({ ...current, overview: undefined })); }}
            placeholder="Enter project overview"
            rows={4}
            maxLength={1000}
            aria-invalid={Boolean(fieldErrors.overview)}
            disabled={saving}
          />
          {fieldErrors.overview ? <div className="invalid-feedback d-block">{fieldErrors.overview}</div> : null}
          <div className="form-text d-flex justify-content-between gap-2"><span>Explain the challenge, your contribution, and the outcome.</span><span>{overview.length}/1000</span></div>
        </div>

        <div className="mb-3">
          <label htmlFor="projectUrl" className="form-label fw-bold">
            Project Link <small className="text-muted">(optional)</small>
          </label>
          <div className="input-group">
          <span className="input-group-text"><i className="bi bi-link-45deg" aria-hidden="true"></i></span>
          <input
            type="text"
            inputMode="url"
            className="form-control"
            id="projectUrl"
            ref={urlInputRef}
            value={projectUrl}
            onChange={(e) => { setProjectUrl(e.target.value); setFieldErrors((current) => ({ ...current, projectUrl: undefined })); }}
            onBlur={(e) => setProjectUrl(normalizeProjectUrl(e.target.value))}
            placeholder="https://example.com"
            disabled={saving}
            aria-invalid={Boolean(fieldErrors.projectUrl)}
          />
          </div>
          {fieldErrors.projectUrl ? <div className="invalid-feedback d-block">{fieldErrors.projectUrl}</div> : null}
          <div className="form-text">Link to a live site, repository, or case study.</div>
        </div>
        </section> : null}

        {activeFormTab === 'skills' ? <section className="portfolio-form-tab-panel" id="project-skills-panel" role="tabpanel" aria-labelledby="project-skills-tab">
          <div className="portfolio-form-section-heading">
            <span className="portfolio-form-section-icon"><i className="bi bi-award" aria-hidden="true"></i></span>
            <div><h4 id="project-skills-heading">Skills demonstrated <span className="portfolio-optional-label">Optional</span></h4><p>Connect this project to relevant skills.</p></div>
          </div>
          <div className="portfolio-skill-chips">
            {skillsUsed.length > 0 ? skillsUsed.map((skillId) => {
              const skill = availableSkills.find((item) => item.id === skillId);
              return <span key={skillId} className="portfolio-skill-chip">{skill?.name || skillId}<button type="button" aria-label={`Remove ${skill?.name || 'skill'}`} onClick={() => setSkillsUsed((current) => current.filter((id) => id !== skillId))} disabled={saving}><i className="bi bi-x" aria-hidden="true"></i></button></span>;
            }) : <div className="portfolio-empty-selection"><i className="bi bi-award" aria-hidden="true"></i><span>No skills linked yet</span></div>}
          </div>
          <button type="button" className="btn btn-sm btn-outline-primary mt-3" onClick={() => setShowSkillsMenu(true)} disabled={saving}>
            <i className="bi bi-plus-lg me-2" />{skillsUsed.length ? 'Change skills' : 'Add skills'}
          </button>
        </section> : null}

        {activeFormTab === 'images' ? <section className="portfolio-form-tab-panel" id="project-images-panel" role="tabpanel" aria-labelledby="project-images-tab">
          <div className="portfolio-form-section-heading">
            <span className="portfolio-form-section-icon"><i className="bi bi-images" aria-hidden="true"></i></span>
            <div><h4 id="project-images-heading">Project images <span className="portfolio-optional-label">Optional</span></h4><p>Show the strongest parts of your work.</p></div>
          </div>

          {/* Current Images List */}
          {images.length > 0 && (
            <div className="card bg-light mb-3 portfolio-project-images">
              <div className="card-body p-2">
                <h6 className="card-title mb-3">
                  Project Images ({images.length})
                </h6>
                <div className="portfolio-project-image-list">
                  {images.map((img, index) => (
                    <div key={index} className="card border-secondary-subtle">
                      <div className="card-body d-flex justify-content-between align-items-start gap-2 p-2">
                        <div className="d-flex align-items-start gap-2 flex-grow-1">
                          <ProjectImagePreview userId={userId} imageId={img.imageId} />
                          <div className="flex-grow-1">
                            <label className="form-label small fw-semibold mb-1" htmlFor={`project-image-description-${index}`}>Image description</label>
                            <input
                              id={`project-image-description-${index}`}
                              type="text"
                              value={img.description || ''}
                              onChange={(e) => handleUpdateImageDescription(index, e.target.value)}
                              placeholder="Add description for this image"
                              disabled={saving}
                              className="form-control form-control-sm"
                              maxLength={IMAGE_DESCRIPTION_MAX_LENGTH}
                            />
                            <small className="text-muted d-block mt-1 text-end">
                              {(img.description || '').length}/{IMAGE_DESCRIPTION_MAX_LENGTH}
                            </small>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleRemoveImage(index)}
                          disabled={saving}
                          aria-label={`Remove image ${index + 1}`}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Add Image Section */}
          <ProjectImageSelector
            userId={userId}
            onAddImage={handleAddImage}
            disabled={saving}
          />
        </section> : null}

        <div className="portfolio-record-form-actions d-flex flex-column flex-sm-row gap-2">
          <button
            type="submit"
            className="btn btn-success flex-grow-1"
            disabled={saving}
          >
            {saving ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Saving...
              </>
            ) : editingProject ? (
              'Update Project'
            ) : (
              'Create Project'
            )}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={saving}
          >
            Cancel
          </button>
        </div>
        {showSkillsMenu ? (
          <div className="new-blog-skills-selection">
            <SkillsGallery
              userId={userId}
              onSelect={setSkillsUsed}
              setExpanded={setShowSkillsMenu}
              multiSelect
              initialSelectedIds={skillsUsed}
            />
          </div>
        ) : null}
    </form>
  );
};

const ProjectImagePreview = ({ userId, imageId }) => {
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadImage = async () => {
      if (!userId || !imageId) {
        setLoading(false);
        return;
      }

      try {
        const imageData = await getImage(userId, imageId);
        if (isMounted) {
          setImage(imageData?.base64 || null);
        }
      } catch (err) {
        if (isMounted) {
          setImage(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadImage();

    return () => {
      isMounted = false;
    };
  }, [userId, imageId]);

  return (
    <div
      className="border rounded bg-light d-flex align-items-center justify-content-center flex-shrink-0"
      style={{ width: '56px', height: '56px', overflow: 'hidden' }}
      title="Image preview"
    >
      {loading ? (
        <span className="spinner-border spinner-border-sm text-secondary" role="status" aria-hidden="true" />
      ) : image ? (
        <img
          src={image}
          alt="Project preview"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <i className="bi bi-image text-muted"></i>
      )}
    </div>
  );
};

export default AddProjectForm;
